import {fetchSafeDocumentFragment} from '@github-ui/fetch-utils'
import {on} from 'delegated-events'
import {observe} from '@github/selector-observer'
import {fromEvent, Subscription} from '@github-ui/subscription'

// Announce report completion
function announceCompletion() {
  const reportStatusElement = document.querySelector<HTMLElement>('.js-report-status')
  if (reportStatusElement) {
    // eslint-disable-next-line i18n-text/no-en
    reportStatusElement.textContent = 'Dormant users report has been completed and is ready for download.'

    // Keep the completion message for a few seconds before clearing
    setTimeout(() => {
      const currentReportStatus = document.querySelector<HTMLElement>('.js-report-status')
      if (currentReportStatus && currentReportStatus.textContent?.includes('completed and is ready for download')) {
        currentReportStatus.textContent = ''
      }
    }, 5000) // Keep message for 5 seconds
  }
}

on('submit', '.js-dormant-users-reports-form', async event => {
  const form = event.currentTarget as HTMLFormElement
  const errorFlash = form.querySelector<HTMLElement>('.js-dormant-users-reports-error')!
  const reportStatus = form.querySelector<HTMLElement>('.js-report-status')!
  errorFlash.hidden = true

  event.preventDefault()

  // Update aria-live region to announce report generation
  // eslint-disable-next-line i18n-text/no-en
  reportStatus.textContent = 'Generating new dormant users report. This may take a few minutes.'

  try {
    const response = await fetchSafeDocumentFragment(document, form.action, {
      method: form.method,
      body: new FormData(form),
    })

    form.replaceWith(response)

    // Check what elements exist after form replacement
    setTimeout(() => {
      const progressContainer = document.querySelector('.js-in-progress-report-status-container')
      const completedReports = document.querySelectorAll('[href*="dormant-users-export"]')
      const newReportStatus = document.querySelector<HTMLElement>('.js-report-status')

      if (progressContainer) {
        // Set up an observer to watch for when the progress container gets removed
        const containerObserver = new MutationObserver(mutations => {
          for (const mutation of mutations) {
            if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
              // Check if the progress container was removed
              for (const removedNode of mutation.removedNodes) {
                if (
                  removedNode === progressContainer ||
                  (removedNode.nodeType === Node.ELEMENT_NODE && (removedNode as Element).contains(progressContainer))
                ) {
                  // Announce completion
                  announceCompletion()

                  // Stop observing since we've detected completion
                  containerObserver.disconnect()
                  break
                }
              }
            }
          }
        })

        // Observe the parent of the progress container for removals
        const containerParent = progressContainer.parentElement
        if (containerParent) {
          containerObserver.observe(containerParent, {
            childList: true,
            subtree: true,
          })
        }

        // Also set up a polling fallback to check if container disappears
        const pollForCompletion = () => {
          const currentContainer = document.querySelector('.js-in-progress-report-status-container')
          if (!currentContainer) {
            const reportStatusElement = document.querySelector<HTMLElement>('.js-report-status')
            if (reportStatusElement) {
              announceCompletion()
            }
          } else {
            // Container still exists, check again in 1 second
            setTimeout(pollForCompletion, 1000)
          }
        }

        // Start polling after a short delay
        setTimeout(pollForCompletion, 2000)
      } else {
        // Original immediate completion logic as fallback
        if (completedReports.length > 0 && newReportStatus) {
          announceCompletion()
        }
      }
    }, 100)
  } catch (error) {
    // Clear the status message on error
    reportStatus.textContent = ''

    // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
    if (error.message.startsWith('HTTP 422')) {
      // eslint-disable-next-line i18n-text/no-en
      errorFlash.textContent = 'Only one report can be in progress at a time.'
    } else {
      // eslint-disable-next-line i18n-text/no-en
      errorFlash.textContent = 'Something went wrong when creating your new report.'
    }
    errorFlash.hidden = false
  }
})

// Observer for progress status containers that appear dynamically
observe('body', {
  subscribe: () => {
    // Use MutationObserver to watch for new elements being added
    const mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element

            // Check if the added element or any of its children has the class we're looking for
            const progressContainer = element.matches?.('.js-in-progress-report-status-container')
              ? element
              : element.querySelector?.('.js-in-progress-report-status-container')

            if (progressContainer) {
              // Set up WebSocket listener for this element
              fromEvent(progressContainer, 'socket:message', async function (event: Event) {
                const data = (event as CustomEvent).detail?.data

                // Check for completion and announce
                const mainReportStatus = document.querySelector<HTMLElement>('.js-report-status')
                if (mainReportStatus && (data?.completed || data?.status === 'completed')) {
                  announceCompletion()
                }
              })
            }
          }
        }
      }
    })

    // Start observing
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })

    // Also set up a fallback that listens for socket messages on the body
    const socketSubscription = fromEvent(document.body, 'socket:message', async function (event: Event) {
      const target = event.target as HTMLElement
      const data = (event as CustomEvent).detail?.data

      if (target?.closest('.js-in-progress-report-status-container')) {
        const mainReportStatus = document.querySelector<HTMLElement>('.js-report-status')
        if (mainReportStatus && (data?.completed || data?.status === 'completed')) {
          announceCompletion()
        }
      }
    })

    // Return a Subscription object with cleanup function
    return new Subscription(() => {
      mutationObserver.disconnect()
      socketSubscription.unsubscribe()
    })
  },
})
